import { NewsletterForm } from "#app/components/newsletter-form.tsx"
import { invariantResponse } from "@epic-web/invariant"
import { useEffect } from "react"
import { type MetaFunction, type LoaderFunctionArgs, data } from "react-router"
import { useLoaderData } from "react-router"
import { Breadcrumbs } from "#app/components/breadcrumbs"
import BlogImage from "#app/components/blog-image.tsx"
import { ShareButtons } from "#app/components/share-buttons"
import { AnalyticsEvents } from "#app/utils/analytics"
import { useTrackScrollDepth } from "#app/utils/analytics-hooks"
import { APP_NAME, BASE_URL, DEFAULT_BLOG_AUTHOR } from "#app/utils/constants"
import {
	generateBlogPostingJsonLd,
	generateBreadcrumbListJsonLd,
} from "#app/utils/json-ld"
import { generateMeta } from "#app/utils/meta"
import { useUmami } from "#app/utils/umami"
import { prisma } from "#app/utils/db.server.ts"
import { getBlogImgSrc } from "#app/utils/misc.tsx"

export async function loader({ params }: LoaderFunctionArgs) {
	const { slug } = params

	invariantResponse(slug, "Slug is required")

	const post = await prisma.post.findUnique({
		where: {
			slug,
			published: true,
		},
		select: {
			id: true,
			title: true,
			slug: true,
			excerpt: true,
			content: true,
			published: true,
			publishedAt: true,
			updatedAt: true,
			author: {
				select: {
					name: true,
				},
			},
			images: {
				select: {
					id: true,
					altText: true,
				},
				take: 1,
			},
		},
	})

	invariantResponse(post, "Post not found")

	return data({ post })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	if (!data?.post) {
		return generateMeta({
			title: "Entrada no encontrada",
			description: "La entrada del blog que buscas no existe.",
		})
	}

	const siteUrl =
		process.env.NODE_ENV === "production"
			? "https://www.gretaspatchwork.com"
			: BASE_URL

	const baseMeta = generateMeta({
		title: `${data.post.title} - ${APP_NAME}`,
		description: data.post.excerpt ?? undefined,
		canonical: `/blog/${data.post.slug}`,
		image: data.post.images[0]
			? `${siteUrl}${getBlogImgSrc(data.post.images[0].id)}`
			: undefined,
	})

	const blogPostingJsonLd = generateBlogPostingJsonLd(data.post)
	const breadcrumbJsonLd = generateBreadcrumbListJsonLd([
		{ name: "Inicio", url: siteUrl },
		{ name: "Blog", url: `${siteUrl}/blog` },
		{ name: data.post.title, url: `${siteUrl}/blog/${data.post.slug}` },
	])

	return [
		...baseMeta,
		{
			"script:ld+json": {
				"@context": "https://schema.org",
				"@graph": [blogPostingJsonLd, breadcrumbJsonLd],
			},
		},
	]
}

export default function BlogPost() {
	const { post } = useLoaderData<typeof loader>()
	const { umami } = useUmami()

	useEffect(() => {
		umami().track(AnalyticsEvents.BLOG_POST_VIEW, {
			post_slug: post.slug,
			post_title: post.title,
		})
	}, [post.slug])

	useTrackScrollDepth(post.slug)

	return (
		<div className="container mx-auto px-4 py-12">
			<Breadcrumbs
				items={[
					{ label: "Inicio", href: "/" },
					{ label: "Blog", href: "/blog" },
					{ label: post.title },
				]}
			/>

			<article className="mx-auto mt-8 max-w-3xl">
				<header className="mb-8">
					<h1 className="font-serif text-4xl text-primary dark:text-primary-foreground">
						{post.title}
					</h1>
					{post.excerpt && (
						<p className="mt-4 text-xl text-muted-foreground dark:text-foreground/70">
							{post.excerpt}
						</p>
					)}
					<div className="mt-6 flex items-center gap-4 text-muted-foreground dark:text-foreground/60">
						<span>{post.author?.name ?? DEFAULT_BLOG_AUTHOR}</span>
						{post.publishedAt && (
							<>
								<span>•</span>
								<time dateTime={post.publishedAt.toISOString()}>
									{new Date(post.publishedAt).toLocaleDateString("es-ES", {
										year: "numeric",
										month: "long",
										day: "numeric",
									})}
								</time>
							</>
						)}
					</div>
					<div className="mt-4">
						<ShareButtons
							url={`/blog/${post.slug}`}
							title={post.title}
							description={post.excerpt ?? undefined}
						/>
					</div>
				</header>

				{post.images[0] && (
					<div className="mb-8 overflow-hidden rounded-lg">
						<BlogImage
							src={getBlogImgSrc(post.images[0].id)}
							alt={post.images[0].altText ?? post.title}
							sizes="(max-width: 768px) 100vw, 800px"
							loading="eager"
						/>
					</div>
				)}

				<div
					className="prose prose-neutral max-w-none dark:prose-invert dark:prose-headings:text-primary-foreground"
					dangerouslySetInnerHTML={{ __html: post.content }}
				/>

				<div className="mt-12 border-t border-border pt-8">
					<NewsletterForm variant="prominent" listType="blog" />
				</div>
			</article>
		</div>
	)
}
